import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, MapPin, Building2, X, Globe } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";

const Arrondissement = () => {
  const [arrondissements, setArrondissements] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [formData, setFormData] = useState({ name: '', details: '', cityId: '', code: '' });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: '' });
  const [profil, setProfil] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({ currentPage: 1, itemsPerPage: 10 });

  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(error => console.error(error));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetchCountries();
    fetchArrondissements();
  }, []);

  // Charger les villes quand un pays est sélectionné
  useEffect(() => {
    if (selectedCountry) {
      fetchCitiesByCountry(selectedCountry);
    } else {
      fetchCities();
    }
  }, [selectedCountry]);

  // Charger les pays
  const fetchCountries = async () => {
    try {
      const response = await AxiosInstance.get('/api/countries?limit=100');
      setCountries(response.data.countries || []);

      // Sélectionner le Cameroun par défaut
      const cameroun = response.data.countries.find(c => c.code === 'CM');
      if (cameroun) {
        setSelectedCountry(cameroun._id);
      }
    } catch (err) {
      console.error('Erreur chargement pays:', err);
    }
  };

  const fetchArrondissements = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCity) params.append('cityId', selectedCity);
      if (selectedCountry && !selectedCity) params.append('countryId', selectedCountry);

      const response = await AxiosInstance.get(`/api/arrondissements?${params}`);
      setArrondissements(response.data.arrondissements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await AxiosInstance.get('/api/cities?limit=100');
      setCities(response.data.cities || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchCitiesByCountry = async (countryId) => {
    try {
      const response = await AxiosInstance.get(`/api/cities?countryId=${countryId}&limit=100`);
      setCities(response.data.cities || []);
      setSelectedCity(''); // Réinitialiser la ville sélectionnée
    } catch (err) {
      setError(err.message);
    }
  };

  // Recharger les arrondissements quand les filtres changent
  useEffect(() => {
    fetchArrondissements();
  }, [selectedCity, selectedCountry]);

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedCity('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setPagination((prev) => ({
      ...prev,
      itemsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Le nom de l\'arrondissement est requis');
      return;
    }

    if (!formData.cityId) {
      setError('La ville est requise');
      return;
    }

    try {
      if (editingItem) {
        await AxiosInstance.put(`/api/arrondissements/${editingItem._id}`, formData);
      } else {
        await AxiosInstance.post('/api/arrondissements', formData);
      }

      await fetchArrondissements();
      resetForm();
      setShowModal(false);
      setSuccessMessage(editingItem ? 'Arrondissement modifié avec succès ✅' : 'Arrondissement créé avec succès ✅');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await AxiosInstance.delete(`/api/arrondissements/${id}`);

      await fetchArrondissements();
      setSuccessMessage('Arrondissement supprimé avec succès ✅');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm({ show: false, id: null, name: '' });
    }
  };

  const handleDeleteRequest = (id, name) => setDeleteConfirm({ show: true, id, name });

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      details: item.details || '',
      cityId: item.cityId?._id || item.cityId,
      code: item.code || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      details: '',
      cityId: selectedCity || '',
      code: ''
    });
    setEditingItem(null);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleAddNew = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      cityId: selectedCity || (cities[0]?._id || '')
    }));
    setShowModal(true);
  };

  // Filtrage + recherche (côté client pour affiner)
  const filteredArrondissements = arrondissements.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.details && item.details.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = pagination.currentPage * pagination.itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - pagination.itemsPerPage;
  const currentArrondissements = filteredArrondissements.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredArrondissements.length / pagination.itemsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading && countries.length === 0) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="card page-header">
          <div className="header-content">
            <div className="header-title-section">
              <div className="header-icon-container">
                <Building2 className="header-icon" />
              </div>
              <div>
                <h1 className="header-title">Gestion des Arrondissements</h1>
                <p className="header-subtitle">Gérer les arrondissements par pays et ville</p>
              </div>
            </div>
            <button onClick={handleAddNew} className="add-button">
              <Plus className="add-button-icon" />
              Nouvel Arrondissement
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card filters-section">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {/* Filtre Pays */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Globe size={18} /> Pays
              </label>
              <select
                value={selectedCountry}
                onChange={handleCountryChange}
                className="form-input"
              >
                <option value="">Tous les pays</option>
                {countries.map(country => (
                  <option key={country._id} value={country._id}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre Ville */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ marginBottom: '0.5rem' }}>
                Ville
              </label>
              <select
                value={selectedCity}
                onChange={handleCityChange}
                className="form-input"
                disabled={!selectedCountry && cities.length === 0}
              >
                <option value="">Toutes les villes</option>
                {cities.map(city => (
                  <option key={city._id} value={city._id}>
                    {city.name} {city.region ? `(${city.region})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Recherche */}
            <div className="search-container" style={{ marginTop: 30 }}>
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un arrondissement..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="search-input"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <strong>Erreur:</strong> {error}
          </div>
        )}

        {/* Tableau */}
        <div className="table-container">
          <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Afficher </label>
              <select
                value={pagination.itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
              </select>
              <span> éléments</span>
            </div>
            <div>
              {filteredArrondissements.length > 0 && (
                <span>
                  Affichage {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} à {Math.min(pagination.currentPage * pagination.itemsPerPage, filteredArrondissements.length)} sur {filteredArrondissements.length}
                </span>
              )}
            </div>
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead className="table-header">
                <tr>
                  <th>Nom</th>
                  <th>Code</th>
                  <th>Ville</th>
                  <th>Pays</th>
                  <th>Détails</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {currentArrondissements.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <MapPin className="empty-icon" />
                      <p className="empty-title">
                        {searchTerm || selectedCity || selectedCountry
                          ? 'Aucun arrondissement trouvé'
                          : 'Aucun arrondissement disponible'}
                      </p>
                      <p className="empty-subtitle">
                        {searchTerm || selectedCity || selectedCountry
                          ? 'Essayez de modifier vos critères de recherche'
                          : 'Commencez par ajouter un nouvel arrondissement'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  currentArrondissements.map((item) => (
                    <tr key={item._id} className="table-row">
                      <td className="table-cell">
                        <strong>{item.name}</strong>
                      </td>
                      <td className="table-cell">{item.code || '-'}</td>
                      <td className="table-cell">
                        {item.cityId?.name || 'N/A'}
                        {item.cityId?.region && (
                          <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '0.5rem' }}>
                            ({item.cityId.region})
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        {item.cityId?.countryId ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>{item.cityId.countryId.flag}</span>
                            <span>{item.cityId.countryId.name}</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="table-cell">
                        {item.details?.length > 40
                          ? `${item.details.substring(0, 40)}...`
                          : item.details || '—'}
                      </td>
                      <td className="table-cell text-right">
                        <div className="actions-container">
                          <button
                            onClick={() => handleEdit(item)}
                            className="action-button edit"
                            title="Modifier"
                          >
                            <Edit2 className="action-icon" />
                          </button>
                          <button
                            onClick={() => handleDeleteRequest(item._id, item.name)}
                            className="action-button delete"
                            title="Supprimer"
                          >
                            <Trash2 className="action-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button
                disabled={pagination.currentPage === 1}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              >
                Précédent
              </button>
              {pageNumbers.map(n => (
                <button
                  key={n}
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: n }))}
                  className={pagination.currentPage === n ? 'active' : ''}
                >
                  {n}
                </button>
              ))}
              <button
                disabled={pagination.currentPage === totalPages}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              >
                Suivant
              </button>
            </div>
          )}
        </div>

        {/* Modal Ajout/Modification */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">
                  {editingItem ? "Modifier l'arrondissement" : "Nouvel arrondissement"}
                </h2>
                <button onClick={handleCloseModal} className="modal-close">
                  <X className="modal-close-icon" />
                </button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* Ville */}
                  <div className="form-group">
                    <label className="form-label">Ville *</label>
                    <select
                      required
                      value={formData.cityId}
                      onChange={(e) => setFormData({ ...formData, cityId: e.target.value })}
                      className="form-input"
                    >
                      <option value="">Sélectionnez une ville</option>
                      {cities.map(city => (
                        <option key={city._id} value={city._id}>
                          {city.countryId?.flag} {city.name} {city.region ? `(${city.region})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Nom */}
                  <div className="form-group">
                    <label className="form-label">Nom de l'arrondissement *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="form-input"
                      placeholder="Ex: Akwa, Bali, Plateau"
                    />
                  </div>

                  {/* Code */}
                  <div className="form-group">
                    <label className="form-label">Code (optionnel)</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="form-input"
                      placeholder="Ex: AKW"
                    />
                  </div>

                  {/* Détails */}
                  <div className="form-group">
                    <label className="form-label">Détails (optionnel)</label>
                    <textarea
                      value={formData.details}
                      onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                      rows="3"
                      className="form-textarea"
                      placeholder="Ajoutez des détails supplémentaires…"
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="modal-button cancel"
                    >
                      Annuler
                    </button>
                    <button type="submit" className="modal-button submit">
                      {editingItem ? 'Modifier' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Succès */}
        {successMessage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Succès</h2>
              </div>
              <div className="modal-body">
                <p>{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Modal Confirmation Suppression */}
        {deleteConfirm.show && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Confirmation</h2>
                <button
                  className="modal-close"
                  onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })}
                >
                  <X className="modal-close-icon" />
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Voulez-vous vraiment supprimer l'arrondissement <strong>{deleteConfirm.name}</strong> ?
                </p>
                <div className="modal-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1rem' }}>
                  <button
                    style={{ backgroundColor: '#6c757d', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })}
                  >
                    Annuler
                  </button>
                  <button
                    style={{ backgroundColor: '#dc3545', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => handleDelete(deleteConfirm.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Arrondissement;